import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { agentsService } from '../services/agentsService';
import { toast } from 'sonner';
import { useAgent } from '../contexts/AgentContext';
import { useAnalytics } from '../hooks/useAnalytics';
import voiceAnalytics from '../utils/voiceAnalytics';
import { 
  ArrowLeft, 
  X, 
  ShoppingCart, 
  Heart, 
  Building2, 
  GraduationCap, 
  Landmark, 
  Cpu, 
  Plane, 
  Utensils, 
  Home, 
 
  Building, 
  Factory, 
 
  Car, 
  Shield, 
  Scale, 
  Dumbbell, 
  Film, 
  CircleHelp,
  Phone,
  TrendingUp,
  BookOpen,
  Calendar,
  Headphones,
  Users,
  Wrench
} from 'lucide-react';

// Industry icons mapping
const industryIcons = {
  'Healthcare': Heart,
  'Finance': Building2,
  'E-commerce & Retail': ShoppingCart,
  'Education': GraduationCap,
  'Government': Landmark,
  'Technology': Cpu,
  'Hospitality & Travel': Plane,
  'Food & Beverage': Utensils,
  'Real Estate': Home,
  'Government & Public Sector': Building,
  'Manufacturing': Factory,
  'Automotive': Car,
  'Insurance': Shield,
  'Legal': Scale,
  'Fitness & Wellness': Dumbbell,
  'Media & Entertainment': Film,
  'Other': CircleHelp
};

// Use case icons mapping
const useCaseIcons = {
  'Customer Support': Headphones,
  'Outbound Sales': TrendingUp,
  'Learning & Development': BookOpen,
  'Scheduling': Calendar,
  'Answering Service': Phone,
  'Lead Qualification': Users,
  'Custom Agent': Wrench
};

// Industry-specific use cases (currently unused)
/*
const industrySpecificUseCases = {
  'Healthcare': [
    { name: 'Patient Appointment Scheduling', description: 'Manage patient bookings and reminders' },
    { name: 'Medical Information Assistant', description: 'Answer health-related queries' },
    { name: 'Insurance Verification', description: 'Verify and process insurance claims' }
  ],
  'Finance': [
    { name: 'Account Management', description: 'Handle account inquiries and transactions' },
    { name: 'Loan Application Assistant', description: 'Guide customers through loan processes' },
    { name: 'Investment Advisor', description: 'Provide investment guidance and updates' }
  ],
  'E-commerce': [
    { name: 'Order Tracking', description: 'Track and update order status' },
    { name: 'Product Recommendations', description: 'Suggest products based on preferences' },
    { name: 'Returns & Refunds', description: 'Process return and refund requests' }
  ],
  'Real Estate': [
    { name: 'Property Viewing Scheduler', description: 'Schedule and manage property viewings' },
    { name: 'Rental Inquiries', description: 'Handle rental property questions' },
    { name: 'Market Analysis Assistant', description: 'Provide market insights and trends' }
  ],
  'Education': [
    { name: 'Student Support', description: 'Answer academic queries and provide resources' },
    { name: 'Enrollment Assistant', description: 'Guide through enrollment processes' },
    { name: 'Course Advisor', description: 'Recommend courses and programs' }
  ]
};
*/

const CreateAgent = () => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  
  // Use agent context
  const {
    currentStep,
    agentData,
    updateAgentData,
    goToNextStep,
    goToPreviousStep,
    initializeAgentCreation,
    trackStepStart,
    trackStepCompleted,
    trackAgentDetailField,
    trackTemplateInteraction,
    trackAgentCreationCompleted,
    trackAgentCreationAbandoned,
    trackValidationError
  } = useAgent();
  
  const { trackButtonClick, trackPageView } = useAnalytics();
  
  // Form data derived from context - should be read-only here, updates go through context
  const selectedIndustry = agentData.businessIndustry || '';
  const selectedIndustryOther = agentData.businessIndustryOther || '';
  const selectedUseCase = agentData.agentType || '';
  const agentName = agentData.agentName || '';
  const language = agentData.language || 'en';
  const website = agentData.website || '';
  const mainGoal = agentData.agentGoals || '';
  const chatOnly = agentData.chatOnly || false;

  // Initialize agent creation on mount
  useEffect(() => {
    initializeAgentCreation('direct_navigation');
    trackPageView('agent_creation', {
      entry_point: 'create_agent_page'
    });
    
    // Track first step start
    if (currentStep === 1) {
      trackStepStart(1, 'industry_selection');
    }
  }, [initializeAgentCreation, trackPageView, trackStepStart, currentStep]);



  const handleIndustrySelect = (industry) => {
    // Update agent context
    updateAgentData({ 
      businessIndustry: industry, 
      businessIndustryOther: industry === 'Other' ? selectedIndustryOther : '' 
    });
    
    // Track industry selection
    trackStepCompleted(1, 'industry_selection');
    trackTemplateInteraction('industry', industry, {
      is_other_option: industry === 'Other'
    });
    
    // Move to next step if not "Other"
    if (industry !== 'Other') {
      goToNextStep();
      trackStepStart(2, 'use_case_selection');
    }
  };

  const handleOtherIndustrySubmit = () => {
    if (selectedIndustryOther.trim()) {
      // Track "Other" industry specification
      trackAgentDetailField('businessIndustryOther', selectedIndustryOther);
      trackStepCompleted(1, 'industry_selection');
      
      // Move to next step
      goToNextStep();
      trackStepStart(2, 'use_case_selection');
    } else {
      trackValidationError(1, 'businessIndustryOther', 'Industry specification required');
    }
  };

  const handleUseCaseSelect = (useCase) => {
    // Update agent context
    updateAgentData({ agentType: useCase });
    
    // Track use case selection
    trackStepCompleted(2, 'use_case_selection');
    trackTemplateInteraction('agent_type', useCase, {
      selected_industry: selectedIndustry === 'Other' ? selectedIndustryOther : selectedIndustry
    });
    
    // Move to next step
    goToNextStep();
    trackStepStart(3, 'agent_configuration');
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Track step abandonment
      trackAgentCreationAbandoned('step_navigation', {
        abandoned_step: currentStep,
        going_back_to: currentStep - 1
      });
      
      goToPreviousStep();
      
      // Track previous step start
      const stepNames = ['', 'industry_selection', 'use_case_selection', 'agent_configuration'];
      trackStepStart(currentStep - 1, stepNames[currentStep - 1]);
    }
  };

  const handleClose = () => {
    // Track agent creation abandonment
    trackAgentCreationAbandoned('user_exit', {
      abandoned_step: currentStep,
      completion_percentage: Math.round(((currentStep - 1) / 3) * 100)
    });
    
    trackButtonClick('close_agent_creation', 'agent_creation', {
      step_abandoned: currentStep
    });
    
    navigate('/agents');
  };

  const generateSystemPrompt = () => {
    const industry = selectedIndustry === 'Other' ? selectedIndustryOther : selectedIndustry;
    const useCase = selectedUseCase;
    const goal = mainGoal;
    
    return `# Personality

You are a highly efficient and professional virtual assistant named ${agentName || 'Assistant'}, specializing in ${useCase.toLowerCase()} for ${industry.toLowerCase()} businesses.
You are detail-oriented, ensuring accurate information capture and assistance.
You are always professional and courteous, reflecting positively on the business you represent.

# Environment

You are ${chatOnly ? 'handling chat conversations' : 'answering phone calls'} on behalf of a ${industry.toLowerCase()} business.
You have access to relevant business systems and information.
You need to gather information from ${chatOnly ? 'users' : 'callers'} and assist them accordingly.

# Tone

Your responses are clear, concise, and professional.
You speak in a friendly and helpful manner, ensuring ${chatOnly ? 'users' : 'callers'} feel valued.
You use a confident and reassuring tone.
You use positive language and avoid jargon.

# Goal

Your primary goal is to ${goal || `efficiently handle ${useCase.toLowerCase()} tasks`}.

# Guardrails

- Stay focused on ${useCase.toLowerCase()} and the business domain
- Do not provide personal opinions or engage in controversial discussions
- If you cannot fulfill a request, politely explain why and offer alternatives
- Do not ask for or reveal sensitive personal information beyond what's necessary
- Maintain professional boundaries at all times`;
  };

  const handleCreateAgent = async () => {
    if (!agentName || !mainGoal) {
      toast.error('Please fill in all required fields');
      
      // Track validation errors
      if (!agentName) trackValidationError(3, 'agentName', 'Agent name is required');
      if (!mainGoal) trackValidationError(3, 'mainGoal', 'Main goal is required');
      
      return;
    }

    setIsCreating(true);
    
    // Track agent creation start
    trackStepCompleted(3, 'agent_configuration');
    trackButtonClick('create_agent_submit', 'agent_creation', {
      agent_name: agentName,
      industry: selectedIndustry === 'Other' ? selectedIndustryOther : selectedIndustry,
      use_case: selectedUseCase,
      language: language,
      chat_only: chatOnly,
      has_website: !!website
    });
    
    try {
      const systemPrompt = generateSystemPrompt();
      const industry = selectedIndustry === 'Other' ? selectedIndustryOther : selectedIndustry;
      
      const agentConfig = {
        name: agentName,
        conversation_config: {
          asr: {
            quality: "high",
            provider: "elevenlabs",
            user_input_audio_format: "pcm_16000",
            keywords: []
          },
          turn: {
            turn_timeout: 7,
            silence_end_call_timeout: -1,
            mode: "turn"
          },
          tts: {
            model_id: language === 'en' ? "eleven_turbo_v2" : "eleven_flash_v2_5",
            voice_id: "cjVigY5qzO86Huf0OWal", // Default voice
            supported_voices: [],
            agent_output_audio_format: "pcm_16000",
            optimize_streaming_latency: 3,
            stability: 0.5,
            speed: 1,
            similarity_boost: 0.8,
            pronunciation_dictionary_locators: []
          },
          conversation: {
            text_only: chatOnly,
            max_duration_seconds: 600,
            client_events: [
              "audio",
              "interruption"
            ]
          },
          language_presets: {},
          agent: {
            first_message: `Hello! I'm ${agentName}, your ${selectedUseCase.toLowerCase()} assistant for ${industry}. How can I help you today?`,
            language: language,
            dynamic_variables: {
              dynamic_variable_placeholders: {}
            },
            prompt: {
              prompt: systemPrompt,
              llm: "gemini-2.0-flash",
              temperature: 0.7,
              max_tokens: -1,
              tool_ids: [],
              built_in_tools: {
                end_call: null,
                language_detection: null,
                transfer_to_agent: null,
                transfer_to_number: null,
                skip_turn: null,
                play_keypad_touch_tone: null,
                voicemail_detection: null
              },
              mcp_server_ids: [],
              native_mcp_server_ids: [],
              knowledge_base: [],
              custom_llm: null,
              ignore_default_personality: false,
              rag: {
                enabled: false,
                embedding_model: "e5_mistral_7b_instruct",
                max_vector_distance: 0.6,
                max_documents_length: 50000,
                max_retrieved_rag_chunks_count: 20
              },
              timezone: null,
              tools: []
            }
          }
        },
        metadata: {
          created_at_unix_secs: Math.floor(Date.now() / 1000)
        },
        platform_settings: {
          auth: {
            enable_auth: false,
            allowlist: [],
            shareable_token: null
          },
          evaluation: {
            criteria: []
          },
          widget: {
            variant: "full",
            placement: "bottom-right",
            expandable: "never",
            avatar: {
              type: "orb",
              color_1: "#2792dc",
              color_2: "#9ce6e6"
            },
            feedback_mode: "during",
            bg_color: "#ffffff",
            text_color: "#000000",
            btn_color: "#000000",
            btn_text_color: "#ffffff",
            border_color: "#e1e1e1",
            focus_color: "#000000",
            border_radius: null,
            btn_radius: null,
            action_text: null,
            start_call_text: null,
            end_call_text: null,
            expand_text: null,
            listening_text: null,
            speaking_text: null,
            shareable_page_text: null,
            shareable_page_show_terms: true,
            terms_text: "#### Terms and conditions\n\nBy clicking \"Agree,\" and each time I interact with this AI agent, I consent to the recording, storage, and sharing of my communications with third-party service providers, and as described in the Privacy Policy.\nIf you do not wish to have your conversations recorded, please refrain from using this service.",
            terms_html: null,
            terms_key: null,
            show_avatar_when_collapsed: false,
            disable_banner: false,
            override_link: null,
            mic_muting_enabled: false,
            transcript_enabled: false,
            text_input_enabled: true,
            default_expanded: false,
            always_expanded: false,
            text_contents: {
              main_label: null,
              start_call: null,
              start_chat: null,
              new_call: null,
              end_call: null,
              mute_microphone: null,
              change_language: null,
              collapse: null,
              expand: null,
              copied: null,
              accept_terms: null,
              dismiss_terms: null,
              listening_status: null,
              speaking_status: null,
              connecting_status: null,
              chatting_status: null,
              input_label: null,
              input_placeholder: null,
              input_placeholder_text_only: null,
              input_placeholder_new_conversation: null,
              user_ended_conversation: null,
              agent_ended_conversation: null,
              conversation_id: null,
              error_occurred: null,
              copy_id: null
            },
            styles: {
              base: null,
              base_hover: null,
              base_active: null,
              base_border: null,
              base_subtle: null,
              base_primary: null,
              base_error: null,
              accent: null,
              accent_hover: null,
              accent_active: null,
              accent_border: null,
              accent_subtle: null,
              accent_primary: null,
              overlay_padding: null,
              button_radius: null,
              input_radius: null,
              bubble_radius: null,
              sheet_radius: null,
              compact_sheet_radius: null,
              dropdown_sheet_radius: null
            },
            language_selector: false,
            supports_text_only: true,
            custom_avatar_path: null,
            language_presets: {}
          },
          data_collection: {},
          overrides: {
            conversation_config_override: {
              tts: {
                voice_id: false
              },
              conversation: {
                text_only: chatOnly
              },
              agent: {
                first_message: false,
                language: false,
                prompt: {
                  prompt: false,
                  native_mcp_server_ids: false
                }
              }
            },
            custom_llm_extra_body: false,
            enable_conversation_initiation_client_data_from_webhook: false
          },
          call_limits: {
            agent_concurrency_limit: -1,
            daily_limit: 100000,
            bursting_enabled: true
          },
          ban: null,
          privacy: {
            record_voice: true,
            retention_days: -1,
            delete_transcript_and_pii: false,
            delete_audio: false,
            apply_to_existing_conversations: false,
            zero_retention_mode: false
          },
          workspace_overrides: {
            conversation_initiation_client_data_webhook: null,
            webhooks: {
              post_call_webhook_id: null,
              send_audio: false
            }
          },
          testing: {
            test_ids: []
          },
          safety: {
            is_blocked_ivc: false,
            is_blocked_non_ivc: false,
            ignore_safety_evaluation: false
          }
        },
        phone_numbers: [],
        workflow: {
          edges: {},
          nodes: {
            start_node: {
              type: "start",
              position: {
                x: 0,
                y: 0
              }
            }
          }
        },
        tags: []
      };

      const result = await agentsService.createAgent(agentConfig);
      
      // Track successful agent creation
      trackAgentCreationCompleted(result.agent_id, 'direct_creation');
      
      toast.success('Agent created successfully!');
      navigate(`/agents/${result.agent_id}`);
    } catch (error) {
      // Track creation failure
      trackAgentCreationAbandoned('creation_error', {
        error_message: error.message,
        error_type: error.name || 'unknown',
        step: 3
      });
      
      toast.error(`Failed to create agent: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const industries = [
    'Healthcare', 'Finance', 'E-commerce & Retail', 'Education', 'Government', 'Technology',
    'Hospitality & Travel', 'Food & Beverage', 'Real Estate',
    'Government & Public Sector', 'Manufacturing', 'Automotive',
    'Insurance', 'Legal', 'Fitness & Wellness', 'Media & Entertainment', 'Other'
  ];

  const constantUseCases = [
    'Customer Support', 'Outbound Sales', 'Learning & Development',
    'Scheduling', 'Answering Service', 'Lead Qualification', 'Custom Agent'
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="flex justify-end mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {console.log('CreateAgent: Rendering with currentStep:', currentStep)}
        {console.log('CreateAgent: Rendering with agentData:', agentData)}

        {/* Step 1: Business Industry */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {console.log('CreateAgent: Rendering Step 1 - Business Industry')}
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-card-foreground">
                What's your business industry?
              </h1>
              <p className="text-muted-foreground">
                Select the industry that best describes your business
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {industries.map((industry) => {
                const Icon = industryIcons[industry];
                return (
                  <Card
                    key={industry}
                    className={`p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${
                      selectedIndustry === industry ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => handleIndustrySelect(industry)}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-xs font-medium text-center leading-tight">
                        {industry}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>

            {selectedIndustry === 'Other' && (
              <div className="mt-6 space-y-4">
                <Input
                  placeholder="Enter your industry"
                  value={selectedIndustryOther}
                  onChange={(e) => {
                    updateAgentData({ businessIndustryOther: e.target.value });
                    trackAgentDetailField('businessIndustryOther', e.target.value);
                  }}
                  className="max-w-md mx-auto"
                />
                <div className="text-center">
                  <Button
                    onClick={handleOtherIndustrySubmit}
                    disabled={!selectedIndustryOther.trim()}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}
            
            {/* Back Button - Step 1 doesn't need back button since it's the first step */}
          </div>
        )}

        {/* Step 2: Use Case */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {console.log('CreateAgent: Rendering Step 2 - Use Case')}
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-card-foreground">
                What type of agent do you need?
              </h1>
              <p className="text-muted-foreground">
                Choose the use case that best fits your needs
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {constantUseCases.map((useCase) => {
                const Icon = useCaseIcons[useCase];
                return (
                  <Card
                    key={useCase}
                    className={`p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${
                      selectedUseCase === useCase ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => handleUseCaseSelect(useCase)}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-xs font-medium text-center leading-tight">
                        {useCase}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>
            
            {/* Back Button */}
            {currentStep > 1 && (
              <div className="flex justify-start mt-8">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Agent Details */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {console.log('CreateAgent: Rendering Step 3 - Agent Details')}
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-card-foreground">
                Configure your agent
              </h1>
              <p className="text-muted-foreground">
                Provide details to customize your AI agent
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="agentName">Agent Name *</Label>
                  <Input
                    id="agentName"
                    placeholder="e.g., Nora, Customer Support Agent"
                    value={agentName}
                    onChange={(e) => {
                      updateAgentData({ agentName: e.target.value });
                      trackAgentDetailField('agentName', e.target.value);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select
                    value={language}
                    onValueChange={(value) => {
                      updateAgentData({ language: value });
                      trackAgentDetailField('language', value);
                    }}
                  >
                    <SelectTrigger id="language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ar">Arabic</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="zh">Chinese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://example.com"
                    value={website}
                    onChange={(e) => {
                      updateAgentData({ website: e.target.value });
                      trackAgentDetailField('website', e.target.value);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mainGoal">Main Goal *</Label>
                  <Textarea
                    id="mainGoal"
                    placeholder="What do you want this agent to accomplish? e.g., Handle customer inquiries 24/7, qualify leads, schedule appointments..."
                    value={mainGoal}
                    onChange={(e) => {
                      updateAgentData({ agentGoals: e.target.value });
                      trackAgentDetailField('agentGoals', e.target.value);
                    }}
                    rows={4}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="chatOnly">Chat Only Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable for text-based conversations only
                    </p>
                  </div>
                  <Switch
                    id="chatOnly"
                    checked={chatOnly}
                    onCheckedChange={(checked) => {
                      updateAgentData({ chatOnly: checked });
                      trackAgentDetailField('chatOnly', checked);
                      
                      // Track voice analytics toggle
                      voiceAnalytics.trackInteractionModeChange(
                        checked ? 'chat_only' : 'voice_enabled',
                        { previous_mode: chatOnly ? 'chat_only' : 'voice_enabled' }
                      );
                    }}
                  />
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleCreateAgent}
                  disabled={isCreating || !agentName || !mainGoal}
                >
                  {isCreating ? 'Creating Agent...' : 'Create Agent'}
                </Button>
              </div>
            </Card>
            
            {/* Back Button */}
            <div className="flex justify-start mt-8">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        )}
        
        {currentStep > 3 && (
          <div className="text-center">
            <p>Invalid step: {currentStep}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAgent;